import React, { ChangeEvent, useCallback, useRef } from "react";
import {
    createStyles,
    TextField,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { Temporal } from "temporal-polyfill";
import {
    formatDateTimeLocal,
    parseDateTimeLocal
} from "../../helpers/auctionDateTime";

type HTMLInputElementWithPicker = HTMLInputElement & {
    showPicker?: () => void;
};

const styles = () =>
    createStyles({
        dateTimeInput: {
            cursor: "pointer",
            "&::-webkit-calendar-picker-indicator": {
                filter: "invert(1)",
                cursor: "pointer"
            },
            "&::-ms-clear": {
                display: "none"
            }
        }
    });

export interface IDateTimePickerProps extends WithStyles<typeof styles> {
    disabled?: boolean;
    error?: boolean;
    fullWidth?: boolean;
    helperText?: React.ReactNode;
    label: string;
    margin?: "none" | "dense" | "normal";
    min?: Temporal.PlainDateTime | null;
    name: string;
    onChange: (value: Temporal.PlainDateTime | null, isValid: boolean) => void;
    required?: boolean;
    value: Temporal.PlainDateTime | null;
}

const DateTimePicker: React.FC<IDateTimePickerProps> = ({
    classes,
    disabled,
    error,
    fullWidth = true,
    helperText,
    label,
    margin = "normal",
    min,
    name,
    onChange,
    required,
    value
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isHandlingClickRef = useRef<boolean>(false);

    const onInputClick = useCallback(() => {
        // Prevent infinite recursion if click() triggers onClick again
        if (isHandlingClickRef.current) {
            return;
        }

        const input = inputRef.current as HTMLInputElementWithPicker | null;
        if (!input) return;

        isHandlingClickRef.current = true;

        try {
            if (input.showPicker) {
                input.showPicker();
            } else {
                input.focus();
                // Only call click() if showPicker is not available, and use a flag to prevent recursion
                input.click();
            }
        } finally {
            // Reset flag after a brief delay to allow any event propagation to complete
            setTimeout(() => {
                isHandlingClickRef.current = false;
            }, 0);
        }
    }, []);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value;
            if (!inputValue) {
                onChange(null, true);
                return;
            }

            const wallTime = parseDateTimeLocal(inputValue);
            if (!wallTime) {
                onChange(null, false);
                return;
            }

            onChange(wallTime, true);
        },
        [onChange]
    );

    const minValue = min ? formatDateTimeLocal(min) : undefined;

    return (
        <TextField
            disabled={disabled}
            error={error}
            fullWidth={fullWidth}
            helperText={helperText}
            label={label}
            margin={margin}
            name={name}
            required={required}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            inputRef={inputRef}
            InputProps={{
                className: classes.dateTimeInput,
                onClick: onInputClick
            }}
            inputProps={{
                min: minValue,
                style: { colorScheme: "dark" }
            }}
            value={formatDateTimeLocal(value)}
            onChange={handleChange}
        />
    );
};

export default withStyles(styles)(React.memo(DateTimePicker));
